import { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";
import VTONCloth from "../assets/pricing/vton-cloth.jpeg";
import VTONModel from "../assets/pricing/vton-model.jpeg";
import VTONResult from "../assets/pricing/vton-result.png";
import RemoveBGBefore from "../assets/pricing/remove-bg-before.jpg";
import RemoveBGAfter from "../assets/pricing/remove-bg-after.png";
import RemoveBGBG from "../assets/pricing/remove-bg-bg.png";
import { ArrowDownIcon, ArrowRightIcon } from "lucide-react";

const HAIRLINE = "var(--hairline)";

const freeWithAccount = [
    "Crop and resize",
    "Gallery",
    "Download",
    "Image management",
];

function Sequence({ steps, caption }) {
    return (
        <div className="mt-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                {steps.map((step, index) => (
                    <Fragment key={step.title}>
                        {index > 0 && (
                            <>
                                <ArrowDownIcon className="mx-auto h-5 w-5 shrink-0 text-graphite sm:hidden" aria-hidden="true" />
                                <ArrowRightIcon className="hidden h-5 w-5 shrink-0 text-graphite sm:block" aria-hidden="true" />
                            </>
                        )}
                        <figure className="min-w-0 flex-1 text-center">
                            <div className="overflow-hidden rounded-lg bg-white">
                                <img
                                    src={step.src}
                                    alt={step.alt}
                                    className="mx-auto h-52 w-full object-contain sm:h-60"
                                />
                            </div>
                            <figcaption className="mt-2 font-Mulish font-semibold text-sm text-secondary">{step.title}</figcaption>
                            <p className="text-[13px] leading-snug text-graphite">{step.detail}</p>
                        </figure>
                    </Fragment>
                ))}
            </div>
            <p className="mt-4 text-center font-Mulish font-semibold text-sm text-primary-700">{caption}</p>
        </div>
    );
}

function Price({ credits, unit }) {
    return (
        <div className="shrink-0 text-left sm:text-right">
            <div className="font-newsreader font-medium text-[40px] leading-none tracking-tight text-secondary tabular-nums">
                {credits}
            </div>
            <div className="mt-1 text-[13px] text-graphite">{unit}</div>
        </div>
    );
}

export default function Pricing() {
    useEffect(() => {
        document.title = "Pricing | TryOnStudio";
    }, []);

    return (
        <div className="h-screen overflow-y-scroll bg-ground font-Mulish text-graphite selection:bg-primary-100 selection:text-secondary">
            <div className="mx-auto max-w-[960px] px-6 pb-28 pt-12 sm:pb-32 sm:pt-16">
                <header className="mb-12 max-w-[42rem]">
                    <h1 className="font-newsreader font-medium text-4xl tracking-tight text-secondary sm:text-5xl">
                        Pay as you go
                    </h1>
                    <p className="mt-4 text-base leading-relaxed">
                        Try 100 credits free, then pay for each run. No subscription.
                    </p>
                    <p className="mt-6 inline-flex rounded-full bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700">
                        100 free credits
                    </p>
                </header>

                <div
                    className="overflow-hidden rounded-2xl bg-white"
                    style={{ boxShadow: `0 0 0 1px ${HAIRLINE}` }}
                >
                    <section className="bg-primary-50 px-6 py-6 sm:px-8">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="max-w-[36rem]">
                                <h2 className="font-Mulish font-semibold text-lg text-secondary">Virtual try-on</h2>
                                <p className="mt-1 text-sm leading-relaxed">
                                    One garment is 60 credits at 2K and 75 at 4K. A full look, 2 to 4 garments, is 120 at 2K.
                                </p>
                            </div>
                            <Price credits={60} unit="per try-on" />
                        </div>
                        <Sequence
                            caption="60 credits"
                            steps={[
                                { src: VTONCloth, alt: "Garment laid flat", title: "Garment", detail: "Upload a clothing item" },
                                { src: VTONModel, alt: "Person who will wear the garment", title: "Person", detail: "Choose or upload a photo" },
                                { src: VTONResult, alt: "The garment worn by the person", title: "Result", detail: "One finished try-on" },
                            ]}
                        />
                    </section>

                    <section className="border-t px-6 py-5 transition-colors duration-150 hover:bg-hover-tint sm:px-8" style={{ borderColor: HAIRLINE }}>
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="max-w-[36rem]">
                                <h2 className="font-Mulish font-semibold text-lg text-secondary">Fashion model</h2>
                                <p className="mt-1 text-sm leading-relaxed">
                                    Create a model, swap who is wearing the clothes, or put a flat-lay on a model. A face photo adds 45.
                                </p>
                            </div>
                            <Price credits={60} unit="per image" />
                        </div>
                    </section>

                    <section className="border-t px-6 py-5 transition-colors duration-150 hover:bg-hover-tint sm:px-8" style={{ borderColor: HAIRLINE }}>
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="max-w-[36rem]">
                                <h2 className="font-Mulish font-semibold text-lg text-secondary">Generate and edit</h2>
                                <p className="mt-1 text-sm leading-relaxed">
                                    A still, a reimagine, or a Studio edit at 2K. 4K is 48.
                                </p>
                            </div>
                            <Price credits={27} unit="per image" />
                        </div>
                    </section>

                    <section className="border-t px-6 py-6 sm:px-8" style={{ borderColor: HAIRLINE }}>
                        <div>
                            <h2 className="font-Mulish font-semibold text-lg text-secondary">Studio</h2>
                            <p className="mt-1 max-w-[36rem] text-sm leading-relaxed">
                                Cut out a product, upscale a still, or write a caption.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {[
                                    ["Background", "4"],
                                    ["Upscale", "48"],
                                    ["Caption", "1"],
                                    ["Crop", "Free"],
                                ].map(([label, credits]) => (
                                    <span
                                        key={label}
                                        className="inline-flex items-baseline gap-1.5 rounded-full bg-ground px-3 py-1 text-sm text-graphite"
                                        style={{ boxShadow: `0 0 0 1px ${HAIRLINE}` }}
                                    >
                                        {label}
                                        <span className="font-Mulish font-semibold text-base text-secondary tabular-nums">{credits}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                        <Sequence
                            caption="4 credits"
                            steps={[
                                { src: RemoveBGBefore, alt: "Photo before the background is removed", title: "Original", detail: "Any product photo" },
                                { src: RemoveBGAfter, alt: "The same photo with the background removed", title: "Cut out", detail: "Subject only" },
                                { src: RemoveBGBG, alt: "The cut-out placed on a new background", title: "Finished", detail: "Ready to use" },
                            ]}
                        />
                    </section>

                    <section className="bg-secondary px-6 py-6 text-white sm:px-8">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="max-w-[36rem]">
                                <h2 className="font-Mulish font-semibold text-lg">Video</h2>
                                <p className="mt-1 text-sm leading-relaxed text-white/80">
                                    8 seconds at 1080p. Shorter clips cost less. 4K is 120 credits a second.
                                </p>
                                <p className="mt-2 text-sm leading-relaxed text-white/80">
                                    100 free credits will not start this clip.
                                </p>
                            </div>
                            <div className="shrink-0 text-left sm:text-right">
                                <div className="font-newsreader font-medium text-[40px] leading-none tracking-tight tabular-nums">640</div>
                                <div className="mt-1 text-[13px] text-white/80">for 8 seconds</div>
                            </div>
                        </div>
                    </section>
                </div>

                <div className="mt-16">
                    <h2 className="font-Mulish font-semibold text-xl text-secondary">Included with every account</h2>
                    <ul className="mt-3 flex flex-col gap-2 text-sm sm:flex-row sm:gap-6">
                        {freeWithAccount.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                    <div className="mt-10 flex justify-end">
                        <Link
                            to="/signup"
                            className="inline-flex items-center rounded-full bg-primary-700 px-7 py-3.5 font-Mulish font-semibold text-base text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)] transition-colors duration-150 hover:bg-primary-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                        >
                            Start with 100 credits
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
